import { z } from 'zod';
import { uuidSchema } from './comum.ts';

/**
 * Catálogo de municípios (tabela global, sem tenant).
 *
 * O endereço referencia `cidadeId` — não existe campo com o nome da cidade
 * digitado, senão "São Paulo", "Sao Paulo" e "S. Paulo" viram três municípios
 * distintos e nenhum relatório por cidade fecha.
 */

export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export const ufSchema = z.enum(UFS);
export type Uf = z.infer<typeof ufSchema>;

export const cidadeSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  estado: ufSchema,
  codigoIbge: z.string(),
});

export type Cidade = z.infer<typeof cidadeSchema>;

/**
 * A resposta da busca: só a lista.
 *
 * Sem o envelope de paginação dos cadastros, e é deliberado: esta rota alimenta
 * um autocompletar com teto de 50 linhas, e ninguém pagina um seletor — quem não
 * achou digita mais uma letra. Paginar aqui obrigaria a tela a montar controles
 * de página dentro de uma caixa de sugestão.
 */
export const listaCidadesSchema = z.object({ data: z.array(cidadeSchema) });
export type ListaCidades = z.infer<typeof listaCidadesSchema>;

/**
 * Busca de cidade. O `limit` é baixo de propósito: a lista alimenta um
 * autocompletar, não uma tela de 5.570 linhas.
 *
 * **`codigoIbge` não é um filtro a mais, é uma busca diferente.** Ele identifica
 * um município no país inteiro, e quem o informa não está procurando: já sabe
 * qual é. Quem o usa é a entrada de nota por XML — a NF-e traz o `cMun` do
 * emitente, e é o único campo do endereço do arquivo que casa com o catálogo sem
 * ambiguidade. O nome não casa: "Sant'Ana do Livramento" contra "Santana do
 * Livramento", "Mogi-Mirim" contra "Moji Mirim", e o `ilike` por prefixo devolve
 * zero linhas justamente nesses.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const listarCidadesQuerySchema = z
  .object({
    uf: ufSchema.optional(),
    q: z.string().trim().min(1).max(100).optional(),
    /**
     * Sete dígitos, como o IBGE publica e como a coluna guarda.
     *
     * O `.regex` recusa aqui o que a rota recusaria com uma consulta vazia — e
     * dizer "código inválido" é melhor que devolver lista vazia, que se lê como
     * "este município não está no catálogo" e manda cadastrar à mão.
     */
    codigoIbge: z
      .string()
      .trim()
      .regex(/^\d{7}$/, 'O código do IBGE tem sete dígitos.')
      .optional(),
    limit: z.coerce.number().int().min(1).max(50).prefault(20),
  })
  .strict();

export type ListarCidadesQuery = z.infer<typeof listarCidadesQuerySchema>;
